import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useMemo,
} from 'react';
import CustomIcon from '../Common/CustomIcon/CustomIcon';

import {
	getSession,
} from 'Vulture/Session';

import {
	isPropertyScoped,
} from 'Vulture/Permissions';

import {
	useRowSelect,
	useSortBy,
	useTable,
	useFilters,
} from 'react-table';

const normalizeSortBy = (sortBy = []) => {
	if (!Array.isArray(sortBy)) {
		return [];
	}
	return sortBy
		.filter((s) => s && typeof s.id === 'string')
		.map((s) => ({ id: s.id, desc: Boolean(s.desc) }));
};

const areSortByEqual = (sortA = [], sortB = []) => {
	if (sortA.length !== sortB.length) return false;
	for (let i = 0; i < sortA.length; i++) {
		if (sortA[i].id !== sortB[i].id || Boolean(sortA[i].desc) !== Boolean(sortB[i].desc)) return false;
	}
	return true;
};

const paginateRows = (rows, page, itemsPerPage) => {
	let sliceStart = (page - 1) * itemsPerPage;
	let sliceEnd = page * itemsPerPage;
	return rows.slice(sliceStart, sliceEnd);
};

const formatTime = (time) => {
	const timeArray = time.split(':');
	let hour = parseInt(timeArray[0], 10);
	const minute = timeArray[1];
	let period = 'AM';

	if (hour === 0) {
		hour = 12;
	} else if (hour === 12) {
		period = 'PM';
	} else if (hour > 12) {
		hour -= 12;
		period = 'PM';
	}

	// Pad minute with leading zeros if needed
	const paddedMinute = minute.padStart(2, '0');

	return `${hour}:${paddedMinute} ${period}`;
};

const formatDate = (date) => {
	const newDate = new Date(date);
	const options = {
		month: '2-digit',
		day:   '2-digit',
		year:  'numeric',
	};
	const formattedDate = newDate.toLocaleDateString('en-US', options);

	return formattedDate;
};

const VultureTable = (props) => {
	const {
		columns,
		data,
		columnsToHide,
		secondaryNavButtons,
		tertiaryNavButtons,
		theadColStyles = () => {},
		filterRowColStyles = () => {},
		tbodyColStyles = () => {},
		cellOnClick = () => {},
		allRowsSelected,
		toggleSelectAllRows,
		listingTable,
		listingConfigUUID,
		listingConfigsWithSystems,
		style = {},
		page,
		itemsPerPage,
		totalResults,
		onFilter = () => {},
		initialState = {},
		filterRowInBody = false,
		showResultsAndRowsDropdown = true,
		showSecondaryNav = true,
		onColumnResizeMouseDown = null,
		isColumnResizable = null,
		onFilteredRowsChange = null,
		controlledSortBy = undefined,
		onSortByChange = null,
		tableScrollStyle = null,
		tableScrollerRefCallback = null,
		topScrollerAfterHeader = false,
		hideVerticalScrollbar = false,
		hideFilterRow = false,
		preventSortRef,
		hideFooter = false,
		hideTopScroller = false,
		hideHorizontalScrollbar = false,
		tbodyRefCallback = null,
		columnClassMap = {},
	} = props;

	const tableScrollerRef = useRef();
	const setTableScrollerRef = useCallback((el) => {
		tableScrollerRef.current = el;
		if (typeof tableScrollerRefCallback === 'function') {
			tableScrollerRefCallback(el);
		}
	}, [ tableScrollerRefCallback, ]);
	const tableTopScrollerRef = useRef();
	const tableTopScrollerChildRef = useRef();
	const theadRef = useRef();
	const tbodyRef = useRef();
	const filterRowRef = useRef(null);
	const inlineTopScrollerWrapperRef = useRef(null);
	const inlineTopScrollerRef = useRef(null);
	const inlineTopScrollerChildRef = useRef(null);
	const hScrollSyncingRef = useRef(false);

	useLayoutEffect(() => {
		if (tableScrollerRef.current && tableTopScrollerRef.current && tableTopScrollerChildRef.current) {
			if (topScrollerAfterHeader) {
				tableTopScrollerRef.current.style.width = `${tableScrollerRef.current.clientWidth}px`;
				tableTopScrollerChildRef.current.style.width = `${tableScrollerRef.current.scrollWidth}px`;
			} else {
				const scrollWidth = tableScrollerRef.current.scrollWidth;
				const clientWidth = tableScrollerRef.current.clientWidth;
				tableTopScrollerChildRef.current.style.width = `${scrollWidth}px`;
				tableTopScrollerRef.current.style.overflowX = scrollWidth > clientWidth ? 'auto' : 'hidden';
			}
		}
	});

	const getResultsText = () => {
		const start = Math.min((page - 1) * itemsPerPage + 1, totalResults);
		const end = Math.min(page * itemsPerPage, totalResults);
		const resultsText = totalResults > 0 ?
			`${start}-${end} of ${totalResults}` :
			'0 results';
		return resultsText;
	};

	const defaultColumn = useMemo(() => {
		return {
			Filter: function DefaultColumnFilter({
				column: {
					id, filterValue, preFilteredRows, setFilter,
				},
			}) {
				const count = preFilteredRows.length;

				const handleChange = (e) => {
					const value = e.target.value || undefined;
					setFilter(value);
					onFilter(value, preFilteredRows);
				};

				if (count === 0) {
					return <></>;
				}

				return (<input
					value={filterValue || ''}
					onChange={handleChange}
					placeholder={`Search ${count} records...`}
					data-test-id={`filter-${id}`}
				/>);
			},
		};
	}, [ onFilter, ]);

	const memoizedColumns = useMemo(() => {
		return columns;
	}, [ columns, ]);

	const memoizedData = useMemo(() => {
		return data;
	}, [ data, ]);

	const memoizedColumnsToHide = useMemo(() => {
		return columnsToHide;
	}, [ columnsToHide, ]);

	const memoizedSecondaryNavButtons = useMemo(() => {
		return secondaryNavButtons || [];
	}, [ secondaryNavButtons, ]);

	const memoizedTertiaryNavButtons = useMemo(() => {
		return tertiaryNavButtons || [];
	}, [ tertiaryNavButtons, ]);

	const mergedInitialState = useMemo(() => {
		const initialHiddenColumns = Array.isArray(initialState?.hiddenColumns) ? initialState.hiddenColumns : [];
		const mergedHiddenColumns = Array.from(new Set([ ...(memoizedColumnsToHide || []), ...initialHiddenColumns, ]));
		return {
			...initialState,
			hiddenColumns: mergedHiddenColumns,
		};
	}, [ initialState, memoizedColumnsToHide, ]);

	const tableInstance = useTable(
		{
			columns:          memoizedColumns,
			data:             memoizedData,
			disableMultiSort: true,
			initialState:     mergedInitialState,
			autoResetFilters: false,
			autoResetSortBy:  false,
			defaultColumn,
		},
		useFilters,
		useSortBy,
		useRowSelect
	);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		rows,
		prepareRow,
		allColumns,
		setSortBy,
		setHiddenColumns,
		state: tableState,
	} = tableInstance;

	const tableSortBy = useMemo(() => {
		return normalizeSortBy(tableState?.sortBy);
	}, [ tableState?.sortBy, ]);

	const mergedHiddenColumnsKey = useMemo(() => {
		const cols = mergedInitialState.hiddenColumns;
		return Array.isArray(cols) ? cols.join('\x00') : '';
	}, [ mergedInitialState.hiddenColumns, ]);

	useEffect(() => {
		if (typeof setHiddenColumns === 'function') {
			setHiddenColumns(mergedInitialState.hiddenColumns);
		}
	}, [ mergedHiddenColumnsKey, setHiddenColumns, ]);

	const availableSortColumnIds = useMemo(() => {
		if (!Array.isArray(allColumns)) {
			return [];
		}

		return allColumns
			.map((column) => {
				return column?.id;
			})
			.filter((columnId) => {
				return typeof columnId === 'string';
			});
	}, [ allColumns, ]);

	const showWarning = isPropertyScoped() && listingTable && listingConfigsWithSystems && !listingConfigsWithSystems.includes(listingConfigUUID);

	let paginatedRows = rows;

	if (page && itemsPerPage) {
		paginatedRows = paginateRows(rows, page, itemsPerPage);
	}

	useLayoutEffect(() => {
		tbodyRefCallback?.(tbodyRef.current);
	}, [ tbodyRefCallback, ]);

	useEffect(() => {
		if (typeof controlledSortBy === 'undefined' || controlledSortBy === null) {
			return;
		}

		const nextSortBy = normalizeSortBy(controlledSortBy).filter((sortConfig) => {
			return availableSortColumnIds.includes(sortConfig.id);
		});

		if (!areSortByEqual(tableSortBy, nextSortBy)) {
			setSortBy(nextSortBy);
		}
	}, [ availableSortColumnIds, controlledSortBy, setSortBy, tableSortBy, ]);

	useEffect(() => {
		if (typeof onSortByChange !== 'function') {
			return;
		}

		onSortByChange(tableSortBy);
	}, [ onSortByChange, tableSortBy, ]);

	const prevFilteredRowOriginalsRef = useRef(null);
	useEffect(() => {
		if (typeof onFilteredRowsChange !== 'function') {
			prevFilteredRowOriginalsRef.current = null;
			return;
		}
		const originals = rows.map((r) => r.original);
		const prev = prevFilteredRowOriginalsRef.current;
		const unchanged = prev !== null &&
			prev.length === originals.length &&
			originals.every((item, i) => item === prev[i]);
		prevFilteredRowOriginalsRef.current = originals;
		if (!unchanged) {
			onFilteredRowsChange(rows);
		}
	}, [ rows, onFilteredRowsChange, ]);

	const syncTopScrollerWidths = useCallback(() => {
		if (
			!topScrollerAfterHeader ||
			!tableScrollerRef.current ||
			!inlineTopScrollerWrapperRef.current ||
			!inlineTopScrollerRef.current ||
			!inlineTopScrollerChildRef.current
		) {
			return;
		}
		const scrollW = tableScrollerRef.current.scrollWidth;
		const visibleW = tableScrollerRef.current.offsetWidth;
		inlineTopScrollerWrapperRef.current.style.width = `${scrollW}px`;
		inlineTopScrollerRef.current.style.width = `${visibleW}px`;
		inlineTopScrollerChildRef.current.style.width = `${scrollW}px`;
	}, [ topScrollerAfterHeader, ]);

	useLayoutEffect(() => {
		if (!topScrollerAfterHeader) return undefined;
		syncTopScrollerWidths();
		if (typeof ResizeObserver === 'undefined' || !tableScrollerRef.current) {
			return undefined;
		}
		const observer = new ResizeObserver(() => {
			syncTopScrollerWidths();
		});
		observer.observe(tableScrollerRef.current);
		return () => {
			observer.disconnect();
		};
	}, [ topScrollerAfterHeader, syncTopScrollerWidths, ]);

	return (
		<>
			{(showWarning) && (
				<div className='noPermissionsWarning'>
				*You do not have permissions to edit these listings.
				</div>
			)}
			{showSecondaryNav && (
				<div className='secondaryNav topNav'>
					{memoizedSecondaryNavButtons.map((button, i) => {
						return (
							<div
								key={`button_${i}`}
								className={button.className}
								onClick={button.onClick}
							>
								{button.icon ? <CustomIcon icon={button.icon}/> : ''}{button.text}
							</div>
						);
					})}
				</div>
			)}

			{!topScrollerAfterHeader && (
				<div
					className={style.tableTopScroller}
					ref={tableTopScrollerRef}
					style={{ display: (hideTopScroller || topScrollerAfterHeader) ? 'none' : undefined }}
					onScroll={(e) => {
						const newPosition = e.target.scrollLeft;
						tableScrollerRef.current.scrollLeft = newPosition;
					}}
				>
					<div
						className={style.tableTopScrollerChild}
						ref={tableTopScrollerChildRef}
					/>
				</div>
			)}
			<div
				id="table-scroll"
				className={`${style.tableScroll}${(topScrollerAfterHeader || hideVerticalScrollbar) && style.tableScrollHideScrollbar ? ` ${style.tableScrollHideScrollbar}` : ''}`}
				ref={setTableScrollerRef}
				style={{ overflowX: hideHorizontalScrollbar ? 'hidden' : undefined, ...(tableScrollStyle || {}) }}
				onScroll={(e) => {
					const newPosition = e.target.scrollLeft;
					if (topScrollerAfterHeader) {
						if (inlineTopScrollerRef.current && !hScrollSyncingRef.current) {
							hScrollSyncingRef.current = true;
							inlineTopScrollerRef.current.scrollLeft = newPosition;
							requestAnimationFrame(() => { hScrollSyncingRef.current = false; });
						}
					} else {
						const topRef = tableTopScrollerRef.current;
						if (topRef) {
							topRef.scrollLeft = newPosition;
						}
					}
				}}
			>
				<div className={style.table} data-test-id="vultureTable" {...getTableProps()}>
					<div className={style.thead} ref={theadRef}>
						{headerGroups.map((headerGroup, i) => {
							return (
								<div
									key={`${i}`}
									className={style.tr}
									{...headerGroup.getHeaderGroupProps()}
								>
									{headerGroup.headers.map((column, j) => {
										const columnId = column.id || column.render('id');
										const showColumnResizeHandle =
										typeof onColumnResizeMouseDown === 'function' && typeof isColumnResizable === 'function' && isColumnResizable(column);

										let columnSortIcon = '';
										if (column.isSorted) {
											columnSortIcon = <CustomIcon icon="caret-up"/>;
										}

										if (column.isSortedDesc) {
											columnSortIcon = <CustomIcon icon="caret-down"/>;
										}

										const rawSortToggleProps = column.getSortByToggleProps() || {};
										const { onClick: sortOnClick, ...sortToggleRestProps } = rawSortToggleProps;

										return (
											<div
												id={columnId}
												data-col-id={columnId}
												key={`${j}`}
												className={`${style.th} ${style[column.render('id')] || ''} ${columnClassMap[column.id] ? (style[columnClassMap[column.id]] || '') : ''}`}
												data-test-id={`th-${column.render('id')}`}
												{...column.getHeaderProps()}
												{...sortToggleRestProps}
												onClick={(e) => {
													if (preventSortRef?.current) return;
													if (sortOnClick) sortOnClick(e);
												}}
												style={theadColStyles(column)}
											>
												<div className={style.thContainer}>
													<div className={style.nameContainer}>
														{column.render('Header')}
													</div>
													<div className={style.iconContainer}>
														{columnSortIcon}
													</div>
												</div>
												{showColumnResizeHandle && (
													<div
														className={style.headerResizer}
														onMouseDown={(e) => {
															e.stopPropagation();
															e.preventDefault();
															onColumnResizeMouseDown(e, columnId);
														}}
														onClick={(e) => {
															e.stopPropagation();
															e.preventDefault();
														}}
														role="separator"
														aria-orientation="vertical"
														title="Resize column"
													>
													</div>
												)}
											</div>
										);
									})}
								</div>
							);
						})}
					</div>

				{!filterRowInBody && !hideFilterRow && (
						<div className={style.filterRow} ref={filterRowRef}>
							{headerGroups.map((headerGroup, i) => {
								return (
									<div
										key={`${i}`}
										className={style.tr}
										{...headerGroup.getHeaderGroupProps()}
									>
										{headerGroup.headers.map((column, j) => {
											const columnId = column.render('id');

											let columnFilter = null;

											if (column.canFilter) {
												columnFilter = <div className={style.thFilter} data-test-id={`thFilter-${column.render('id')}`}>{column.render('Filter')}</div>;
											} else if (columnId === 'rowSelect') {
												columnFilter = column.render('Header');
											} else if (columnId === 'imgElm') {
												columnFilter = <div className={style.imgElmFilterIcon}><CustomIcon icon="image"/></div>;
											} else if (typeof columnId === 'string' && columnId.startsWith('checkbox')) {
												columnFilter = <div className={allRowsSelected ? style.activeCheckbox : style.disabledSquare} onClick={(e) => {
													e.preventDefault();
													toggleSelectAllRows(allRowsSelected);
												}}>
													<div className={style.checkboxIcon}>
														<CustomIcon icon="check"/>
													</div>
												</div>;
											}

											return (
												<div
													id={column.render('id')}
													key={`${j}`}
													className={`${style.th} ${style[`${column.render('id')}`] || ''}`}
													{...column.getHeaderProps()}
													style={filterRowColStyles(column)}
													>
														<div className={style.thContainer}>
															{columnFilter}
														</div>
													</div>
												);
											})}
									</div>
								);
							})}
						</div>)}
					{topScrollerAfterHeader && (
						<div ref={inlineTopScrollerWrapperRef}>
							<div
								className={style.tableTopScrollerInline}
								ref={inlineTopScrollerRef}
								onScroll={(e) => {
									if (hScrollSyncingRef.current) return;
									hScrollSyncingRef.current = true;
									tableScrollerRef.current.scrollLeft = e.target.scrollLeft;
									requestAnimationFrame(() => { hScrollSyncingRef.current = false; });
								}}
							>
								<div className={style.tableTopScrollerChild} ref={inlineTopScrollerChildRef} />
							</div>
						</div>
					)}
					<div className={style.tbody} {...getTableBodyProps()} ref={tbodyRef} data-test-id="tableBody">
						{filterRowInBody && (() => {
							const bottomHeaderGroup = headerGroups[headerGroups.length - 1];
							if (!bottomHeaderGroup) return null;
							return (
								<div key="filter_row" className={`${style.tr} ${style.filterRowInBody ? style.filterRowInBody : ''}`} {...bottomHeaderGroup.getHeaderGroupProps()}>
									{bottomHeaderGroup.headers.map((column, j) => {
										const columnId = column.render('id');
										let columnFilter = null;

										if (column.canFilter) {
											columnFilter = <div className={style.thFilter}>{column.render('Filter')}</div>;
										} else if (columnId === 'rowSelect') {
											columnFilter = column.render('Header');
										} else if (typeof columnId === 'string' && columnId.startsWith('checkbox')) {
											columnFilter = <div className={allRowsSelected ? style.activeCheckbox : style.disabledSquare} data-test-id="selectAllCheckbox" onClick={(e) => {
												e.preventDefault();
												toggleSelectAllRows(allRowsSelected);
											}}>
												<div className={style.checkboxIcon}>
													<CustomIcon icon="check"/>
												</div>
											</div>;
										}

										return (
											<div
												key={`filter_${j}`}
												className={`${style.td} ${style[`${column.render('id')}`]}`}
												style={filterRowColStyles(column)}
											>
												<div className={style.tdContainer}>
													{columnFilter}
												</div>
											</div>
										);
									})}
								</div>
							);
						})()}
						{topScrollerAfterHeader && (
							<div
								className={style.tableTopScrollerInline}
								ref={tableTopScrollerRef}
								onScroll={(e) => {
									const newPosition = e.target.scrollLeft;
									tableScrollerRef.current.scrollLeft = newPosition;
								}}
							>
								<div
									className={style.tableTopScrollerChild}
									ref={tableTopScrollerChildRef}
								/>
							</div>
						)}
						{paginatedRows.map((row, i) => {
							prepareRow(row);
							const rowProps = typeof props.rowProps === 'function' ? props.rowProps(row) : {};
							const RowComponent = rowProps.Component || 'div';
							const rowPropsWithoutComponent = {
								...rowProps,
							};
							delete rowPropsWithoutComponent.Component;

							return (
								<RowComponent
									key={`row_${i}`}
									className={style.tr}
									role='row'
									{...rowPropsWithoutComponent}
								>
									{row.cells.map((cell, j) => {
										let cellColumnId = cell.render('id');
										let cellRendered = cell.render('Cell');
										let listingColumn = props.fd[`listing_column.${cellColumnId}`];
										let columnId = '';

										if (listingColumn) {
											for (let [ m1Key, ] of Object.entries(listingColumn)) {
												if (m1Key.startsWith('listing_column_type.')) {
													columnId = m1Key.split('.')[1];
												}
											}
										}

										let listing_column_type = props.fd[`listing_column_type.${columnId}`];

										if (cellColumnId === 'resolution') {
											return <div
												key={`${cellColumnId}_${j}`}
												data-col-id={cellColumnId}
												className={`${style.td} ${style[`${cellColumnId}`] || ''}`}
												data-test-id={`cell-${cellColumnId}`}
												{...cell.getCellProps()}
												style={tbodyColStyles(cell)}
											>
												<div className={style.tdContainer} data-test-id={`cellContainer-${cellColumnId}`}>
													{(cellRendered.props.value || '').replace(/px/g, '')}
												</div>
											</div>;
										}

										if (listing_column_type?.type === 'datetime' && cellRendered.props.value) {
											return <div
												key={`${cellColumnId}_${j}`}
												data-col-id={cellColumnId}
												onClick={cellOnClick(cell, row)}
												className={`${style.td} ${style[`${cellColumnId}`] || ''} ${columnClassMap[cellColumnId] ? (style[columnClassMap[cellColumnId]] || '') : ''}`}
												data-test-id={`cell-${cellColumnId}`}
												{...cell.getCellProps()}
												style={tbodyColStyles(cell)}
											>
												<div className={style.tdContainer} data-test-id={`cellContainer-${cellColumnId}`}>
													{formatDate(cellRendered.props.value)}
												</div>
											</div>;
										}
										if (listing_column_type?.type === 'time') {
											let time = cell.value;

											return <div
												key={`${cellColumnId}_${j}`}
												data-col-id={cellColumnId}
												onClick={cellOnClick(cell, row)}
												className={`${style.td} ${style[`${cellColumnId}`] || ''} ${columnClassMap[cellColumnId] ? (style[columnClassMap[cellColumnId]] || '') : ''}`}
												data-test-id={`cell-${cellColumnId}`}
												{...cell.getCellProps()}
												style={tbodyColStyles(cell)}
											>
												<div className={style.tdContainer} data-test-id={`cellContainer-${cellColumnId}`}>
													{time && formatTime(time)}
												</div>
											</div>;
										}
										return <div
											key={`${cellColumnId}_${j}`}
											data-col-id={cellColumnId}
											onClick={cellOnClick(cell, row)}
											className={`${style.td} ${style[`${cellColumnId}`] || ''} ${columnClassMap[cellColumnId] ? (style[columnClassMap[cellColumnId]] || '') : ''}`}
											data-test-id={`cell-${cellColumnId}`}
											{...cell.getCellProps()}
											style={tbodyColStyles(cell)}
										>
											<div className={style.tdContainer} data-test-id={`cellContainer-${cellColumnId}`}>
												{cellRendered}
											</div>
										</div>;
									})}
								</RowComponent>
							);
						})}
					</div>
				</div>
			</div>

			{showResultsAndRowsDropdown && !hideFooter && (
				<div className={`secondaryNav${style.secondaryNav ? ` ${style.secondaryNav}` : ''}`}>
					<div className={style.resultsText}>
						{getResultsText()}
					</div>
					<div className={style.paginationContainer}>
						{memoizedTertiaryNavButtons.map((button, i) => {
							return (
								<div
									key={`button_${i}`}
									className={button.className}
									onClick={button.onClick}
								>
									{button.icon ? <CustomIcon icon={button.icon}/> : ''}{button.text}
								</div>
							);
						})}
					</div>
					<div className={style.rowsPerPageDropdown}>
						<select
							value={props.itemsPerPage}
							onChange={(e) => {
								const newValue = parseInt(e.target.value, 10);
								props.onItemsPerPageChange(newValue);
							}}
						>
							{[ 5, 10, 25, 50, 100, ].map((option) => {
								return (
									<option key={option} value={option}>
										{option} per page
									</option>
								);
							})}
						</select>
					</div>
				</div>
			)}
		</>
	);
};

export { normalizeSortBy, areSortByEqual };
export default VultureTable;